import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import api from '~/services/api';
import { Colors } from '~/config/Colors';
import { EditorWrapper, EditorToolbar, ToolbarButton, EditorContainer } from './styles';

const TipTapEditor = ({ value, onChange, onImageUploaded }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextStyle,
      Color,
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const addImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async e => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        alert('Imagem muito grande (máx 10MB)');
        return;
      }

      let loadingToast = null;

      try {
        loadingToast = document.createElement('div');
        loadingToast.textContent = 'Fazendo upload da imagem...';
        loadingToast.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${Colors?.Color4 || '#4285f4'};
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          z-index: 9999;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        `;
        document.body.appendChild(loadingToast);

        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(
          '/signed/empresa/gestorprojeto/comunicado/upload-image',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        );

        const imageUrl = response.data.url;

        if (editor) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
          console.log('📝 Imagem inserida no editor');

          if (onImageUploaded && response.data.fileName) {
            onImageUploaded(response.data.fileName);
          } else if (!onImageUploaded) {
            console.warn('⚠️ onImageUploaded callback não foi fornecido!');
          } else if (!response.data.fileName) {
            console.warn('⚠️ fileName não retornado pela API!');
          }

          // Verificar se a imagem foi renderizada no DOM
          setTimeout(() => {
            const images = document.querySelectorAll('.ProseMirror img, .tiptap-image');
            console.log('Imagens encontradas no DOM:', images.length);
            images.forEach((img, idx) => {
              console.log(`Imagem ${idx}:`, {
                src: img.src,
                display: getComputedStyle(img).display,
                width: img.width,
                height: img.height,
                visible: img.offsetWidth > 0 && img.offsetHeight > 0,
              });
            });
          }, 100);
        }
      } catch (error) {
        console.error('Erro ao fazer upload da imagem:', error);
        console.error('Detalhes do erro:', error.response?.data);
        alert('Erro ao fazer upload da imagem. Tente novamente.');
      } finally {
        if (loadingToast && document.body.contains(loadingToast)) {
          document.body.removeChild(loadingToast);
        }
      }
    };

    input.click();
  };

  const addLink = () => {
    const url = window.prompt('Digite a URL:');

    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  if (!editor) return null;

  return (
    <EditorWrapper>
      <EditorToolbar>
        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          title="Título 1"
        >
          H1
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          title="Título 2"
        >
          H2
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          title="Título 3"
        >
          H3
        </ToolbarButton>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 8px' }} />

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Negrito"
        >
          <strong>B</strong>
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Itálico"
        >
          <em>I</em>
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Riscado"
        >
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </ToolbarButton>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 8px' }} />

        <div style={{ position: 'relative' }}>
          <ToolbarButton
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="Cor do texto"
          >
            <span style={{ fontWeight: 'bold' }}>A</span>
            <span
              style={{
                fontSize: '10px',
                color: editor.getAttributes('textStyle').color || '#000',
                marginLeft: '2px',
              }}
            >
              ▼
            </span>
          </ToolbarButton>
          {showColorPicker && (
            <div
              style={{
                position: 'absolute',
                top: '40px',
                left: '0',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '8px',
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: '4px',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              {[
                '#000000',
                '#FFFFFF',
                '#333333',
                '#666666',
                '#999999',
                '#CCCCCC',
                '#FF0000',
                '#FF6B00',
                '#FFD700',
                '#00FF00',
                '#0000FF',
                '#4B0082',
                '#9400D3',
                '#FF1493',
                '#00CED1',
                '#32CD32',
                '#FF4500',
                '#FFA500',
                '#FFFF00',
                '#7FFF00',
                '#1E90FF',
              ].map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setColor(color).run();
                    setShowColorPicker(false);
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    background: color,
                    border:
                      editor.getAttributes('textStyle').color === color
                        ? '2px solid #000'
                        : '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  title={color}
                />
              ))}
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
                style={{
                  gridColumn: 'span 5',
                  padding: '4px',
                  background: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Remover cor
              </button>
            </div>
          )}
        </div>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 8px' }} />

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Lista com marcadores"
        >
          •
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Lista numerada"
        >
          1.
        </ToolbarButton>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 8px' }} />

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
          title="Citação"
        >
          &quot;
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={editor.isActive('codeBlock') ? 'is-active' : ''}
          title="Bloco de código"
        >
          {'<>'}
        </ToolbarButton>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 8px' }} />

        <ToolbarButton
          type="button"
          onClick={addLink}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Adicionar link"
        >
          🔗
        </ToolbarButton>

        {editor.isActive('link') && (
          <ToolbarButton type="button" onClick={removeLink} title="Remover link">
            🔗✖
          </ToolbarButton>
        )}

        <ToolbarButton type="button" onClick={addImage} title="Adicionar imagem">
          📷
        </ToolbarButton>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 8px' }} />

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Linha horizontal"
        >
          ―
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Desfazer"
        >
          ↶
        </ToolbarButton>

        <ToolbarButton
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Refazer"
        >
          ↷
        </ToolbarButton>
      </EditorToolbar>

      <EditorContainer>
        <EditorContent editor={editor} />
        {/* {!value && <div className="placeholder">{placeholder}</div>} */}
      </EditorContainer>
    </EditorWrapper>
  );
};

export default TipTapEditor;
